import { InfiniteData, QueryClient } from '@tanstack/react-query';

import { dateRangesOverlap, getVisibleDateRange } from '../index';

/**
 * Page response with date range metadata for tracking pagination state.
 */
interface PageWithDateRange {
  _dateRange?: { startDate: string; endDate: string };
  [key: string]: unknown;
}

/**
 * Invalidates only the assignment queries that overlap with the visible date range.
 * This prevents refetching non-visible data and maintains scroll position.
 *
 * Strategy:
 * 1. Find all assignment queries
 * 2. For each query, keep only pages that overlap with visible range
 * 3. Invalidate the filtered queries (now only visible pages will refetch)
 *
 * Refetch scope is 'active': only a currently-mounted planner query refetches
 * immediately. Every other cached filter-combination entry (planner queries live
 * forever — staleTime/gcTime Infinity) is merely marked invalidated and refetches
 * lazily when the planner mounts it again. 'all' here caused a request storm:
 * a team-member/time-off mutation refetched dozens of unmounted filter combos.
 *
 * Falls back to invalidating all assignment queries if visible range cannot be determined.
 *
 * @param queryClient - TanStack Query client instance
 */
export function invalidateVisibleAssignments(queryClient: QueryClient): void {
  const visibleRange = getVisibleDateRange();

  if (visibleRange) {
    const queryKeys = [
      ['assignments-by-member'],
      ['assignments-by-department'],
      ['assignments-by-project'],
    ];

    queryKeys.forEach(queryKey => {
      // Get all queries matching this key (there might be multiple with different filters)
      const queries = queryClient.getQueriesData({ queryKey });

      queries.forEach(([key, data]) => {
        if (!data) return;

        const queryData = data as InfiniteData<PageWithDateRange>;
        if (!queryData?.pages) return;

        // Filter pages to keep only those that overlap with visible range
        const visiblePages: PageWithDateRange[] = [];
        const visiblePageParams: unknown[] = [];

        queryData.pages.forEach((page, index) => {
          if (!page?._dateRange) {
            // Keep pages without date range metadata (safety fallback)
            visiblePages.push(page);
            visiblePageParams.push(queryData.pageParams[index]);
            return;
          }

          if (dateRangesOverlap(page._dateRange, visibleRange)) {
            visiblePages.push(page);
            visiblePageParams.push(queryData.pageParams[index]);
          }
        });

        // Update cache with only visible pages
        if (visiblePages.length > 0) {
          queryClient.setQueryData(key, {
            ...queryData,
            pages: visiblePages,
            pageParams: visiblePageParams,
          });
        }
      });

      // Now invalidate — the mounted query (if any) refetches now; unmounted
      // filter combos are marked invalidated and refetch on next mount.
      queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
    });
  } else {
    // Fallback: invalidate all if we can't determine visible range
    queryClient.invalidateQueries({ queryKey: ['assignments-by-member'], refetchType: 'active' });
    queryClient.invalidateQueries({
      queryKey: ['assignments-by-department'],
      refetchType: 'active',
    });
    queryClient.invalidateQueries({ queryKey: ['assignments-by-project'], refetchType: 'active' });
  }
}
